'use client';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://api-v2.teraboxdl.site/api';

// ─── Token helpers ────────────────────────────────────────────────────────────
export const getToken = (): string | null =>
  typeof window !== 'undefined' ? localStorage.getItem('userToken') : null;

export const getAdminToken = (): string | null =>
  typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;

export const clearTokens = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('userToken');
    localStorage.removeItem('adminToken');
  }
};

// ─── Base fetch helpers ───────────────────────────────────────────────────────
async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    
    // Handle cases where response might not be JSON
    const contentType = res.headers.get("content-type");
    let data: any;
    if (contentType && contentType.includes("application/json")) {
      data = await res.json();
    } else {
      const text = await res.text();
      throw new Error(`Invalid response format: ${text.substring(0, 100)}`);
    }

    if (!data.success) {
      if (res.status === 401) {
        clearTokens();
        if (typeof window !== 'undefined') window.location.href = '/sign-up-login-screen';
      }
      throw new Error(data.message || 'Request failed');
    }
    return data;
  } catch (error: any) {
    // If it's already an error we threw, rethrow it
    if (error.message && (error.message.includes('Request failed') || error.message.includes('Invalid response'))) {
      throw error;
    }
    // Handle raw network errors (Failed to fetch)
    throw new Error(error.message || 'Network error: Check your connection or backend status');
  }
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authApi = {
  signup: (email: string, password: string, turnstileToken: string) =>
    apiFetch('/public/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstileToken }),
    }),

  login: (email: string, password: string, turnstileToken: string) =>
    apiFetch('/public/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstileToken }),
    }),

  adminLogin: (email: string, password: string, turnstileToken: string) =>
    apiFetch('/public/admin-auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstileToken }),
    }),
};

// ─── Client ───────────────────────────────────────────────────────────────────
export const clientApi = {
  getDashboard: () =>
    apiFetch('/client/dashboard', {}, getToken()),

  regenerateKey: () =>
    apiFetch('/client/regenerate-key', { method: 'POST' }, getToken()),

  getRequestHistory: () =>
    apiFetch('/client/request-history', {}, getToken()),
};

// ─── Admin ────────────────────────────────────────────────────────────────────
export const adminApi = {
  getStats: () =>
    apiFetch('/admin/stats', {}, getAdminToken()),

  getUsers: () =>
    apiFetch('/admin/users', {}, getAdminToken()),

  createUser: (email: string) =>
    apiFetch(`/admin/create-user?email=${encodeURIComponent(email)}`, { method: 'POST' }, getAdminToken()),

  deleteUser: (email: string) =>
    apiFetch(`/admin/delete-user?email=${encodeURIComponent(email)}`, { method: 'DELETE' }, getAdminToken()),

  clearUsers: () =>
    apiFetch('/admin/clear-users', { method: 'DELETE' }, getAdminToken()),

  toggleStatus: (email: string) =>
    apiFetch(`/admin/toggle-status?email=${encodeURIComponent(email)}`, { method: 'PUT' }, getAdminToken()),

  addBalance: (email: string, amount: number) =>
    apiFetch(`/admin/add-balance?email=${encodeURIComponent(email)}&amount=${amount}`, { method: 'POST' }, getAdminToken()),

  regenerateUserKey: (email: string) =>
    apiFetch(`/admin/regenerate-key?email=${encodeURIComponent(email)}`, { method: 'POST' }, getAdminToken()),

  getUserTransactions: (email: string) =>
    apiFetch(`/admin/user-transactions?email=${encodeURIComponent(email)}`, {}, getAdminToken()),

  updatePlan: (planId: number, newPrice: number, newLimitSec: number, newQuota: number) =>
    apiFetch(
      `/admin/update-plan?planId=${planId}&newPrice=${newPrice}&newLimitSec=${newLimitSec}&newQuota=${newQuota}`,
      { method: 'PUT' },
      getAdminToken()
    ),

  setupPlans: () =>
    apiFetch('/admin/setup-plans', { method: 'POST' }, getAdminToken()),

  getRequestLogs: () =>
    apiFetch('/admin/requests/logs', {}, getAdminToken()),

  getAnalyticsToday: () =>
    apiFetch('/admin/analytics/today', {}, getAdminToken()),
};

// ─── Cookies ──────────────────────────────────────────────────────────────────
export const cookieApi = {
  getAll: (type?: string) =>
    apiFetch(`/admin/cookies${type ? `?type=${type}` : ''}`, {}, getAdminToken()),

  getStats: () =>
    apiFetch('/admin/cookies/stats', {}, getAdminToken()),

  addSingle: (cookieValue: string, cookieType: 'REGULAR' | 'PREMIUM') =>
    apiFetch('/admin/cookies/add', {
      method: 'POST',
      body: JSON.stringify({ cookieValue, cookieType }),
    }, getAdminToken()),

  bulkAdd: (cookies: string[], cookieType: 'REGULAR' | 'PREMIUM') =>
    apiFetch('/admin/cookies/bulk', {
      method: 'POST',
      body: JSON.stringify({ cookies, cookieType }),
    }, getAdminToken()),

  bulkText: (text: string, type: 'REGULAR' | 'PREMIUM' = 'REGULAR') =>
    fetch(`${API_BASE}/admin/cookies/bulk-text?type=${type}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        Authorization: `Bearer ${getAdminToken()}`,
      },
      body: text,
    }).then(async (res) => {
      const data = await res.json();
      if (!data.success) {
        if (res.status === 401) {
          clearTokens();
          if (typeof window !== 'undefined') window.location.href = '/sign-up-login-screen';
        }
        throw new Error(data.message || 'Request failed');
      }
      return data;
    }),

  reload: () =>
    apiFetch('/admin/cookies/reload', { method: 'POST' }, getAdminToken()),

  deleteById: (id: number) =>
    apiFetch(`/admin/cookies/${id}`, { method: 'DELETE' }, getAdminToken()),

  deleteByType: (cookieType: 'REGULAR' | 'PREMIUM') =>
    apiFetch(`/admin/cookies/type/${cookieType}`, { method: 'DELETE' }, getAdminToken()),
};

// ─── Proxy ────────────────────────────────────────────────────────────────────
export const proxyApi = {
  getInfo: () =>
    apiFetch('/admin/proxy/info', {}, getAdminToken()),

  toggle: () =>
    apiFetch('/admin/proxy/toggle', { method: 'POST' }, getAdminToken()),

  updateConfig: (payload: {
    proxyEnabled: boolean;
    proxyType: 'EVOMI' | 'BRIGHTDATA' | 'DECODO';
    preferredCookieType: 'REGULAR' | 'PREMIUM';
  }) =>
    apiFetch('/admin/proxy/config', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }, getAdminToken()),
};

// ─── System ───────────────────────────────────────────────────────────────────
export const systemApi = {
  getHealth: () =>
    apiFetch('/admin/system/health', {}, getAdminToken()),

  getCacheStats: () =>
    apiFetch('/admin/cache/stats', {}, getAdminToken()),

  flushCache: () =>
    apiFetch('/admin/cache/flush', { method: 'POST' }, getAdminToken()),

  getRpsMetrics: () =>
    apiFetch('/admin/metrics/rps', {}, getAdminToken()),
};

// ─── Payment ──────────────────────────────────────────────────────────────────
export const paymentApi = {
  createRechargeLink: (amount: number) =>
    apiFetch('/payment/recharge', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }, getToken()),
};
